import { FormName, FormStatus, StatusBarType } from '../../../models/enums.models';
import { StateService } from '../../../services/state.service';
import { Component, Input, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { FormGroup, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'minster-portal-progress',
    standalone: true,
    imports: [MatIconModule, MatCardModule, CommonModule],
    templateUrl: './minster-portal-progress.component.html',
    styleUrls: ['./minster-portal-progress.component.css']
})

export class MinsterPortalProgressComponent implements OnInit {
    @Input('form') _form?: NgForm;
    @Input('readOnly') _readOnly: boolean = false;

    _items: ProgressItem[] = [
        { name1: 'Your', name2: 'Details', status: FormStatus.Empty, type: StatusBarType.YourDetails, current: false },
        { name1: 'Your', name2: 'Accident', status: FormStatus.Empty, type: StatusBarType.YourAccident, current: false },
        { name1: 'Your', name2: 'Injuries', status: FormStatus.Empty, type: StatusBarType.YourInjuries, current: false },
        { name1: 'Your', name2: 'Vehicle', status: FormStatus.Empty, type: StatusBarType.YourVehicle, current: false },
        { name1: 'Involved', name2: 'Parties', status: FormStatus.Empty, type: StatusBarType.InvolvedParties, current: false }
    ];

    constructor(
        private stateService: StateService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.updateActiveSection();
            });

        if (this._form) {
            this._form.valueChanges?.subscribe(() => {
                this.updateAllSections();
            });
        }

        this.updateActiveSection();
    }
    
    updateActiveSection(): void {
        const currentPath = this.router.url;

        this._items.forEach(item => {
            const sectionPaths = this.getSectionPaths(item.type!);
            item.current = sectionPaths.some(path => currentPath.includes(path));
        });
    }

    getSectionPaths(type: StatusBarType): string[] {
        switch (type) {
            case StatusBarType.YourDetails:
                return ['/form/Personal', '/form/Employment'];
            case StatusBarType.YourAccident:
                return ['/form/YourAccidentYourInvolvement', '/form/YourAccidentTimeLocation', '/form/YourAccidentDescription'];
            case StatusBarType.YourInjuries:
                return ['/form/YourInjuriesDetails', '/form/YourInjuriesMedicalAttention'];
            case StatusBarType.YourVehicle:
                return ['/form/YourVehicle'];
            case StatusBarType.InvolvedParties:
                return ['/form/ResponsibleParty', '/form/Passengers', '/form/PoliceAndWitnesses'];
            default:
                return [];
        }
    }

    updateAllSections(): void {
        this.updateSection(StatusBarType.YourDetails, [
            FormName.Personal,
            FormName.Employment
        ]);
        this.updateSection(StatusBarType.YourAccident, [
            FormName.YourAccidentInvolvement,
            FormName.YourAccidentLocation,
            FormName.YourAccidentDescription
        ]);
        this.updateSection(StatusBarType.YourInjuries, [
            FormName.YourInjuriesDetails,
            FormName.YourInjuriesMedicalAttention
        ]);
        this.updateSection(StatusBarType.YourVehicle, [
            FormName.YourVehicle
        ]);

        if (this.stateService.claim!.numberOfPassengers === 0) {
            this.updateSection(StatusBarType.InvolvedParties, [
                FormName.ResponsibleParty,
                FormName.PoliceAndWitnesses
            ]);
        } else {
            this.updateSection(StatusBarType.InvolvedParties, [
                FormName.ResponsibleParty,
                FormName.Passengers,
                FormName.PoliceAndWitnesses
            ]);
        }

       
    }
    
    updateSection(type: StatusBarType, fields: string[]): void {
        const statuses = fields.map(field => this.getFormGroupStatus(this._form!.controls[field] as FormGroup));
        const sectionStatus = this.getStatusForList(statuses);
        this.updateStatusBarItem(type, sectionStatus);
    }

    updateStatusBarItem(type: StatusBarType, status: FormStatus): void {        
        const item = this._items.find(x => x.type === type);

        if(this.stateService.formIsSubmitted){
           if (item) { 
            item.status = FormStatus.Completed;
            item.previousItemComplete = this.previousItemIsComplete(item.type!);
            return;
           }
        }

        if (item) {
            item.status = status;
            item.previousItemComplete = this.previousItemIsComplete(item.type!);
        }
    }

    getFormGroupStatus(group: FormGroup): FormStatus {
        if (!group) {
            return FormStatus.Empty;
        } 
        else if (group.valid) {
            return FormStatus.Completed;
        } 
        else if (this.stateService.getFormValidationErrorsExcludeRequired(group) > 0) {
            return FormStatus.Error;
        }
        else if (this.stateService.getFormGroupIsPopulated(group)) {
            return FormStatus.Incomplete;
        } 
        else {
            return FormStatus.Empty;
        }
    }

    getStatusForList(list: FormStatus[]): FormStatus {
        if (list.every(x => x === FormStatus.Completed)) {
            return FormStatus.Completed;
        } 
        else if (list.some(x => x === FormStatus.Error)) {
            return FormStatus.Error;
        } 
        else if (list.every(x => x === FormStatus.Empty)) {
            return FormStatus.Empty;
        }

        return FormStatus.Incomplete;
    }

    previousItemIsComplete(type: StatusBarType): boolean {
        const index = this._items.findIndex(item => item.type === type);
        
        if(this.stateService.formIsSubmitted){
           if (index > 0) { 
             return this._items[index - 1].status === FormStatus.Completed; 
           }
        }

        if (index > 0) {
            return this._items[index - 1].status === FormStatus.Completed;
        }

        return true;
    }

    getSectionProgress(item: ProgressItem): number {
        if (item.status === FormStatus.Completed) {
            return 100;
        } 
        else if (item.status === FormStatus.Error) {
            return 75;
        } 
        else if (item.status === FormStatus.Incomplete) {
            return 50;
        } 
        else {
            return 0;
        }
    }
}

class ProgressItem {
    name1?: string;
    name2?: string;
    status?: FormStatus;
    type?: StatusBarType;
    current: boolean = false;
    previousItemComplete?: boolean;
}
